import { IconName } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/pro-solid-svg-icons";
import { StatusCode } from "api/protocols";
import { UserLogout } from "api/rpc";
import classNames from "classnames";
import { ButtonNew as Button } from "components/buttonNew";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { useAppDispatch } from "hooks/redux";
import useOutsideAlerter from "hooks/useOutsideAlerter/useOutsideAlerter";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import React, { useRef, useState, useEffect } from "react";
import { NavLink, useHistory, useRouteMatch } from "react-router-dom";
import { showError, showSuccess } from "redux/actions/ui";
import { IFacilityPortalExtended, useTeeOnPortalContext } from "../TeeOnPortalContext";
import "./teeOnPortalCourses.scss";
import Input from "components/form/input/Input";
import { IFacility } from "redux/reducers/models/facility";
import Spin from "components/spin/spin";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { DeleteFacilityFavourite, PostFacilityFavourite } from "api/rpc/2024-04/customer/facility";
import { sortFacilities } from "redux/reducers/clientPortal";
import ReactDOM from "react-dom";

interface ICourseState {
  courseSearch: string;
  facilities: Array<IFacility>;
}

export default function TeeOnPortalCourses() {
  const { t } = useTranslation();
  const history = useHistory();
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const [loadingFacilityId, setLoadingFacilityId] = useState<number>(null);
  const dispatch = useAppDispatch();

  const [courseState, setCourseState] = useState<ICourseState>({
    courseSearch: "",
    facilities: teeOnPortalState?.facilities,
  });

  useEffect(() => {
    setCourseState(prevState => ({ ...prevState, facilities: teeOnPortalState?.facilities }));
  }, [teeOnPortalState.facilities]);

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setCourseState(prevState => ({ ...prevState, courseSearch: value }));
  }

  function navigateToURL(course: IFacility) {
    if (course) {
      updateTeeOnPortal({ selectedFacilities: [course] });
      history.push("/booking/teetimes/" + course.short_name);
    }
  }

  async function handleAddToFavourites(e: React.MouseEvent, facility: IFacilityPortalExtended) {
    e.stopPropagation();
    setLoadingFacilityId(facility?.id);
    const favRes = await PostFacilityFavourite({ facility_id: facility?.id }, false);
    setLoadingFacilityId(null);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding course to favourites")));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess(t("Successfully added course to favourites")));
  }

  async function handleRemoveFromFavourites(e: React.MouseEvent, facility: IFacilityPortalExtended) {
    e.stopPropagation();
    setLoadingFacilityId(facility?.id);
    const favRes = await DeleteFacilityFavourite({ facility_id: facility?.id }, false);
    setLoadingFacilityId(null);
    if (favRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error removing course from favourites")));
      return;
    }
    void updateFavouriteStatus(facility);
    dispatch(showSuccess(t("Successfully removed course from favourites")));
  }

  function updateFavouriteStatus(selectedFacility: IFacilityPortalExtended) {
    const updatedFacilities = teeOnPortalState?.facilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const updatedSelectedFacilities = teeOnPortalState?.selectedFacilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );
    const updatedCoursesList = courseState?.facilities?.map(facility =>
      facility?.id === selectedFacility?.id ? { ...facility, favourite: !facility?.favourite } : facility,
    );

    const selectedTeeTime = {
      ...teeOnPortalState?.selectedTeeTime,
      facility: { ...selectedFacility, favourite: !selectedFacility?.favourite },
    };
    const sortedFacilities = sortFacilities([...updatedFacilities]);
    ReactDOM.unstable_batchedUpdates(() => {
      updateTeeOnPortal({
        facilities: sortedFacilities,
        selectedTeeTime: selectedTeeTime,
        selectedFacilities: updatedSelectedFacilities,
      });
      setCourseState(prevState => ({ ...prevState, facilities: updatedCoursesList }));
    });
  }

  return (
    <div className="flex flex-col">
      <div className="tee-on-booking-portal-course-page-container">
        <div className="tee-on-booking-portal-course-page-container_title_container">
          <div>
            <p className="tee-on-booking-portal-course-page-container_tee_on_heading">Powered By Tee On</p>
          </div>
          <div>
            <p className="tee-on-booking-portal-course-page-container_courses_heading">Participating Golf Courses</p>
          </div>
          <div>
            <p className="tee-on-booking-portal-course-page-container_description">
              Check out our participating course list below. Tee On continually adds new courses to our network.
            </p>
          </div>
          <div className="tee-on-booking-portal-course-page-container_search_input">
            <Input
              value={courseState.courseSearch ?? ""}
              id="course_search"
              onChange={handleInputChange}
              placeholder={t("Search by Name, City, Postal Code")}
            />
          </div>
        </div>
      </div>

      <div
        className={classNames("tee-on-booking-portal-course-page-container_courses_container", {
          "tee-on-booking-portal-course-page-container_courses_container_limited":
            courseState.facilities?.filter(
              facility =>
                facility?.long_name.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()) ||
                facility?.city.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()) ||
                facility?.postal.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()),
            ).length === 2,
        })}
      >
        {!courseState?.facilities ? (
          <div style={{ display: "flex", height: "100%", margin: "auto" }}>
            <div className="text-center m-auto">
              <div style={{ height: "32px" }}>
                <Spin />
              </div>
              <div>Loading courses...</div>
            </div>
          </div>
        ) : (
          courseState.facilities
            ?.filter(
              facility =>
                facility?.long_name.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()) ||
                facility?.city.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()) ||
                facility?.postal.toLocaleLowerCase().includes(courseState.courseSearch.toLocaleLowerCase()),
            )
            .map((course: IFacility, index: number) => {
              //Display loading when removing/adding course from favourites
              const favouriteLoading = course?.id === loadingFacilityId;
              return (
                <div
                  onClick={() => navigateToURL(course)}
                  key={index}
                  className="tee-on-booking-portal-course-page-container_course_container"
                >
                  <div className="tee-on-booking-portal-course-page-container_header_container">
                    <div className="tee-on-booking-portal-course-page-container_header">
                      <div>
                        <img
                          className="tee-on-booking-portal-course-page-container_logo"
                          src={course.logo_source}
                          alt="Tee-On Logo"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 mb-2 flex flex-col w-full">
                    <p className="tee-on-booking-portal-course-page-container_course_title">
                      {course.long_name}
                      {!!teeOnPortalState?.user &&
                        (favouriteLoading ? (
                          <div style={{ height: "24px", width: "24px" }}>
                            <Spin />
                          </div>
                        ) : (
                          <OverlayTrigger
                            placement="auto"
                            overlay={
                              <Tooltip id="facilityFavouritesToolTip">
                                {course?.favourite ? t("Remove from favourites") : t("Add to favourites")}
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon
                              onClick={
                                loadingFacilityId
                                  ? e => e.stopPropagation()
                                  : course?.favourite
                                  ? e => handleRemoveFromFavourites(e, course)
                                  : e => handleAddToFavourites(e, course)
                              }
                              size="1x"
                              fixedWidth
                              className={classNames("tee-on-booking-portal-course-page-container_unfavourite", {
                                "tee-on-booking-portal-course-page-container_favourite": course?.favourite,
                              })}
                              icon={faStar}
                            />
                          </OverlayTrigger>
                        ))}
                    </p>
                    <span className="tee-on-booking-portal-course-page-container_course_information">
                      {course.city}, {course.province_name}, {course.country_name}{" "}
                    </span>
                  </div>

                  <div className="flex justify-start w-full">
                    <button
                      onClick={() => navigateToURL(course)}
                      className="tee-on-booking-portal-course-page-container_course_button"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              );
            })
        )}
      </div>
    </div>
  );
}
